/**
 * Minimal i18n for kiosk + display screens.
 * Languages: French (default), Malagasy.
 */
import React, { createContext, useContext, useEffect, useState } from "react";

const STRINGS = {
  fr: {
    "lang.name": "Français",
    "lang.short": "FR",
    "kiosk.select_agency": "Sélectionnez votre agence",
    "kiosk.select_agency_hint": "Touchez l'agence où vous êtes accueilli aujourd'hui",
    "kiosk.welcome": "Bienvenue",
    "kiosk.choose_motif": "Sélectionnez le motif de votre visite",
    "kiosk.change_agency": "Changer",
    "kiosk.agency": "Agence",
    "kiosk.priority": "Priorité",
    "kiosk.touch_to_take": "Touchez pour prendre un ticket",
    "kiosk.welcome_clientele": "Accueil clientèle",
    "kiosk.optional_phone": "Numéro de téléphone (optionnel)",
    "kiosk.phone_hint": "Pour recevoir un SMS quand votre tour approche",
    "kiosk.skip": "Passer",
    "kiosk.confirm": "Confirmer",
    "ticket.your_ticket": "Votre ticket",
    "ticket.issued_at": "Émis à",
    "ticket.agency": "Agence",
    "ticket.thanks": "Merci de patienter. Vous serez appelé à votre tour.",
    "ticket.print": "Imprimer",
    "ticket.close": "Fermer",
    "ticket.print_thermal": "Imprimer (thermique)",
    "display.now_serving": "En cours de service",
    "display.upcoming": "Prochains",
    "display.waiting_count": "{n} en attente",
    "display.no_ticket": "Aucun ticket appelé",
    "display.empty_queue": "File vide",
    "display.also_called": "Aussi appelés",
    "display.select_agency": "Sélectionnez l'agence à afficher",
    "display.display_screen": "Écran d'affichage",
    "display.ticket_called": "Ticket appelé",
    "display.counter": "Guichet",
    "display.category": "Catégorie",
    "display.last_called": "Derniers tickets appelés",
    "display.upcoming_by_cat": "Prochains tickets par catégorie",
    "display.no_recent_calls": "Aucun appel récent",
    "display.fullscreen": "Plein écran",
    "display.exit_fullscreen": "Quitter le plein écran",
    "display.sound_on": "Son activé",
    "display.sound_off": "Son coupé",
    "display.please_proceed": "Veuillez vous présenter au guichet",
  },
  mg: {
    "lang.name": "Malagasy",
    "lang.short": "MG",
    "kiosk.select_agency": "Mifidiana ny biraonao",
    "kiosk.select_agency_hint": "Tsindrio ny birao izay andraisana anao androany",
    "kiosk.welcome": "Tongasoa",
    "kiosk.choose_motif": "Mifidiana ny anton'ny fitsidihanao",
    "kiosk.change_agency": "Hanova",
    "kiosk.agency": "Birao",
    "kiosk.priority": "Lohalaharana",
    "kiosk.touch_to_take": "Tsindrio mba haka tapakila",
    "kiosk.welcome_clientele": "Fandraisana mpanjifa",
    "kiosk.optional_phone": "Laharana finday (raha tianao)",
    "kiosk.phone_hint": "Handraisanao SMS rehefa manakaiky ny anjaranao",
    "kiosk.skip": "Hilalao",
    "kiosk.confirm": "Hanamafy",
    "ticket.your_ticket": "Ny tapakilanao",
    "ticket.issued_at": "Navoaka tamin'ny",
    "ticket.agency": "Birao",
    "ticket.thanks": "Mahandrasa azafady. Hantsoina ianao rehefa tonga ny anjaranao.",
    "ticket.print": "Atontavo",
    "ticket.close": "Hidio",
    "ticket.print_thermal": "Atontavo (mafana)",
    "display.now_serving": "Tantanina izao",
    "display.upcoming": "Manaraka",
    "display.waiting_count": "{n} miandry",
    "display.no_ticket": "Tsy misy tapakila antsoina",
    "display.empty_queue": "Tsy misy mpiandry",
    "display.also_called": "Voantso koa",
    "display.select_agency": "Mifidiana ny birao haseho",
    "display.display_screen": "Efitra fampisehoana",
    "display.ticket_called": "Tapakila voantso",
    "display.counter": "Pejy",
    "display.category": "Sokajy",
    "display.last_called": "Tapakila voantso farany",
    "display.upcoming_by_cat": "Manaraka isaky ny sokajy",
    "display.no_recent_calls": "Tsy mbola nisy antso",
    "display.fullscreen": "Efijery feno",
    "display.exit_fullscreen": "Avoaka efijery feno",
    "display.sound_on": "Misy feo",
    "display.sound_off": "Tsy misy feo",
    "display.please_proceed": "Mandrosoa any amin'ny pejy",
  },
};

const I18nContext = createContext({ lang: "fr", setLang: () => {}, t: (k) => k });

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(() => localStorage.getItem("mama.lang") || "fr");

  useEffect(() => {
    localStorage.setItem("mama.lang", lang);
    document.documentElement.lang = lang;
  }, [lang]);

  const setLang = (l) => setLangState(l);

  const t = (key, vars = {}) => {
    const dict = STRINGS[lang] || STRINGS.fr;
    let s = dict[key] ?? STRINGS.fr[key] ?? key;
    Object.entries(vars).forEach(([k, v]) => { s = s.replace(`{${k}}`, v); });
    return s;
  };

  return <I18nContext.Provider value={{ lang, setLang, t }}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  return useContext(I18nContext);
}

export const LANGUAGES = [
  { code: "fr", label: "Français" },
  { code: "mg", label: "Malagasy" },
];
